// Shared non-component exports; keep component modules valid Fast Refresh boundaries.
import { type ReactNode } from "react";
import { buildUrl } from "../lib/router";
import { linkifyCallIds, type CallRefMap } from "../lib/linkifyCallIds";

export const THEMATIC_BREAK = /^\s*(?:(?:-\s*){3,}|(?:\*\s*){3,}|(?:_\s*){3,})$/;

export type Block =
  | { kind: "h2"; text: string }
  | { kind: "h3"; text: string }
  | { kind: "p"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "table"; header: string[]; rows: string[][] }
  | { kind: "code"; text: string };

// Exported for tests (thematic breaks).
export function parseMarkdownBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line === undefined) break;
    if (!line.trim()) {
      i++;
      continue;
    }
    if (/^```/.test(line)) {
      const buf: string[] = [];
      i++;
      while (i < lines.length) {
        const codeLine = lines[i];
        if (codeLine === undefined || /^```/.test(codeLine)) break;
        buf.push(codeLine);
        i++;
      }
      if (i < lines.length) i++;
      blocks.push({ kind: "code", text: buf.join("\n") });
      continue;
    }
    // A thematic break carries no content, only separation — and every
    // `##` heading in these reports already renders its own bottom rule,
    // so drawing an <hr> would double up: the model puts a break
    // immediately above almost every heading. Dropping it is what makes
    // the report look right. Before this, it fell through to the
    // paragraph branch and reached the screen (and the clipboard HTML) as
    // literal dashes.
    if (THEMATIC_BREAK.test(line)) {
      i++;
      continue;
    }
    const h2 = /^##\s+(.+)$/.exec(line);
    if (h2 && h2[1] !== undefined) {
      blocks.push({ kind: "h2", text: h2[1].trim() });
      i++;
      continue;
    }
    const h3 = /^###\s+(.+)$/.exec(line);
    if (h3 && h3[1] !== undefined) {
      blocks.push({ kind: "h3", text: h3[1].trim() });
      i++;
      continue;
    }
    const tableSep = lines[i + 1];
    if (
      line.includes("|") &&
      tableSep !== undefined &&
      /^\s*\|?\s*:?-{2,}/.test(tableSep) &&
      tableSep.includes("|")
    ) {
      const header = splitTableRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length) {
        const rowLine = lines[i];
        if (rowLine === undefined || !rowLine.trim() || !rowLine.includes("|")) {
          break;
        }
        rows.push(splitTableRow(rowLine));
        i++;
      }
      blocks.push({ kind: "table", header, rows });
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length) {
        const itemLine = lines[i];
        if (itemLine === undefined || !/^\s*\d+\.\s+/.test(itemLine)) break;
        items.push(itemLine.replace(/^\s*\d+\.\s+/, ""));
        i++;
      }
      blocks.push({ kind: "ol", items });
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length) {
        const itemLine = lines[i];
        if (itemLine === undefined || !/^\s*[-*]\s+/.test(itemLine)) break;
        items.push(itemLine.replace(/^\s*[-*]\s+/, ""));
        i++;
      }
      blocks.push({ kind: "ul", items });
      continue;
    }
    const buf: string[] = [line];
    i++;
    while (i < lines.length) {
      const contLine = lines[i];
      if (
        contLine === undefined ||
        !contLine.trim() ||
        /^#{2,3}\s+/.test(contLine) ||
        /^\s*([-*]|\d+\.)\s+/.test(contLine) ||
        /^```/.test(contLine) ||
        THEMATIC_BREAK.test(contLine)
      ) {
        break;
      }
      buf.push(contLine);
      i++;
    }
    blocks.push({ kind: "p", text: buf.join(" ") });
  }
  return blocks;
}

export function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  return s.split("|").map((c) => c.trim());
}

// Exported for tests (PET-645 — formatting-variant call-id citations).
export function renderInline(text: string, refMap: CallRefMap): ReactNode {
  const nodes: ReactNode[] = [];
  const re = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      nodes.push(
        ...linkifyCallIds(text.slice(last, m.index), `p${key++}`, refMap),
      );
    }
    const tok = m[0];
    if (tok.startsWith("**")) {
      nodes.push(
        <strong key={`b${key++}`}>
          {linkifyCallIds(tok.slice(2, -2), `b${key}`, refMap)}
        </strong>,
      );
    } else if (tok.startsWith("`")) {
      nodes.push(
        <code
          key={`c${key++}`}
          className="rounded bg-muted/60 px-1 py-0.5 text-[0.8em]"
        >
          {linkifyCallIds(tok.slice(1, -1), `c${key}`, refMap)}
        </code>,
      );
    } else if (tok.startsWith("[")) {
      const lm = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      const linkText = lm?.[1];
      const linkHref = lm?.[2];
      if (linkText !== undefined && linkHref !== undefined) {
        const isExternal = /^https?:\/\//i.test(linkHref);
        nodes.push(
          <a
            key={`l${key++}`}
            href={linkHref}
            {...(isExternal
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
            className="text-primary underline underline-offset-2 hover:text-primary/80"
          >
            {linkText}
          </a>,
        );
      } else {
        nodes.push(tok);
      }
    } else {
      nodes.push(
        <em key={`i${key++}`}>
          {linkifyCallIds(tok.slice(1, -1), `i${key}`, refMap)}
        </em>,
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) {
    nodes.push(...linkifyCallIds(text.slice(last), `p${key++}`, refMap));
  }
  return <>{nodes}</>;
}

// Build a copy payload with both rich HTML (clickable links) and plain
// text fallback. The HTML mirrors the rendered markdown — headings,
// lists, tables, paragraphs, bold/italic, and inline links — with call
// IDs swapped for `<phone> (<n>)` anchors pointing at absolute URLs.
// Exported for tests (PET-645 — formatting-variant call-id citations).
export function buildCopyPayload(
  markdown: string,
  refMap: CallRefMap,
  stamp: { formatted: string; id: string } | null,
  agentName: string,
): { html: string; text: string } {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";
  const callHref = (callId: string) =>
    `${origin}${buildUrl("call-detail", null, null, { callId })}`;

  const blocks = parseMarkdownBlocks(markdown);
  const htmlParts: string[] = [];
  const textParts: string[] = [];

  const title = `Training tips for ${agentName}`;
  htmlParts.push(`<h2>${escapeHtml(title)}</h2>`);
  textParts.push(title);
  textParts.push("");

  if (stamp) {
    htmlParts.push(
      `<p><strong>Generated:</strong> ${escapeHtml(stamp.formatted)}<br>` +
        `<strong>Report ID:</strong> <code>${escapeHtml(stamp.id)}</code></p>`,
    );
    textParts.push(`Generated: ${stamp.formatted}`);
    textParts.push(`Report ID: ${stamp.id}`);
    textParts.push("");
  }

  for (const b of blocks) {
    switch (b.kind) {
      case "h2":
        htmlParts.push(`<h2>${inlineHtml(b.text, refMap, callHref)}</h2>`);
        textParts.push(inlineText(b.text, refMap, callHref));
        break;
      case "h3":
        htmlParts.push(`<h3>${inlineHtml(b.text, refMap, callHref)}</h3>`);
        textParts.push(inlineText(b.text, refMap, callHref));
        break;
      case "p":
        htmlParts.push(`<p>${inlineHtml(b.text, refMap, callHref)}</p>`);
        textParts.push(inlineText(b.text, refMap, callHref));
        break;
      case "ul":
        htmlParts.push(
          `<ul>${b.items
            .map((it) => `<li>${inlineHtml(it, refMap, callHref)}</li>`)
            .join("")}</ul>`,
        );
        for (const it of b.items) {
          textParts.push(`- ${inlineText(it, refMap, callHref)}`);
        }
        break;
      case "ol":
        htmlParts.push(
          `<ol>${b.items
            .map((it) => `<li>${inlineHtml(it, refMap, callHref)}</li>`)
            .join("")}</ol>`,
        );
        b.items.forEach((it, i) => {
          textParts.push(`${i + 1}. ${inlineText(it, refMap, callHref)}`);
        });
        break;
      case "code":
        htmlParts.push(`<pre><code>${escapeHtml(b.text)}</code></pre>`);
        textParts.push(b.text);
        break;
      case "table": {
        const thead = `<thead><tr>${b.header
          .map((h) => `<th>${inlineHtml(h, refMap, callHref)}</th>`)
          .join("")}</tr></thead>`;
        const tbody = `<tbody>${b.rows
          .map(
            (r) =>
              `<tr>${r
                .map(
                  (c) => `<td>${inlineHtml(c, refMap, callHref)}</td>`,
                )
                .join("")}</tr>`,
          )
          .join("")}</tbody>`;
        htmlParts.push(
          `<table border="1" cellpadding="4" cellspacing="0">${thead}${tbody}</table>`,
        );
        textParts.push(b.header.join(" | "));
        for (const r of b.rows) {
          textParts.push(
            r.map((c) => inlineText(c, refMap, callHref)).join(" | "),
          );
        }
        break;
      }
    }
    textParts.push("");
  }

  const html = `<!doctype html><html><body>${htmlParts.join("")}</body></html>`;
  const text = textParts.join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";
  return { html, text };
}

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function inlineHtml(
  text: string,
  refMap: CallRefMap,
  callHref: (id: string) => string,
): string {
  const out: string[] = [];
  const re = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      out.push(linkifyHtml(text.slice(last, m.index), refMap, callHref));
    }
    const tok = m[0];
    if (tok.startsWith("**")) {
      out.push(`<strong>${linkifyHtml(tok.slice(2, -2), refMap, callHref)}</strong>`);
    } else if (tok.startsWith("`")) {
      out.push(`<code>${linkifyHtml(tok.slice(1, -1), refMap, callHref)}</code>`);
    } else if (tok.startsWith("[")) {
      const lm = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      const linkText = lm?.[1];
      const linkTarget = lm?.[2];
      if (linkText !== undefined && linkTarget !== undefined) {
        const href = /^https?:\/\//i.test(linkTarget)
          ? linkTarget
          : `${typeof window !== "undefined" ? window.location.origin : ""}${linkTarget}`;
        out.push(`<a href="${escapeHtml(href)}">${escapeHtml(linkText)}</a>`);
      } else {
        out.push(escapeHtml(tok));
      }
    } else {
      out.push(`<em>${linkifyHtml(tok.slice(1, -1), refMap, callHref)}</em>`);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) {
    out.push(linkifyHtml(text.slice(last), refMap, callHref));
  }
  return out.join("");
}

export function linkifyHtml(
  text: string,
  refMap: CallRefMap,
  callHref: (id: string) => string,
): string {
  const re = /\b\d{15,19}\b/g;
  let out = "";
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out += escapeHtml(text.slice(last, m.index));
    const id = m[0];
    const info = refMap.get(id);
    const href = callHref(id);
    const label =
      info && info.contact_phone && info.phone_ordinal != null
        ? `${info.contact_phone} (${info.phone_ordinal})`
        : id;
    out += `<a href="${escapeHtml(href)}">${escapeHtml(label)}</a>`;
    last = m.index + id.length;
  }
  if (last < text.length) out += escapeHtml(text.slice(last));
  return out;
}

export function inlineText(
  text: string,
  refMap: CallRefMap,
  callHref: (id: string) => string,
): string {
  const cleaned = text
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_full, label, url) => {
      const absolute = /^https?:\/\//i.test(url)
        ? url
        : `${typeof window !== "undefined" ? window.location.origin : ""}${url}`;
      return `${label} (${absolute})`;
    })
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1");
  return cleaned.replace(/\b\d{15,19}\b/g, (id) => {
    const info = refMap.get(id);
    const href = callHref(id);
    const label =
      info && info.contact_phone && info.phone_ordinal != null
        ? `${info.contact_phone} (${info.phone_ordinal})`
        : id;
    return `${label} (${href})`;
  });
}
